import { ServiceTemplateSchedule } from "@common/types/serviceTemplateSchedule";
import {
    ServiceTemplateSchedulesSortValue,
    ServiceTemplateSchedulesViewColumn,
} from "@common/types/views/serviceTemplateSchedulesView";
import { ViewConfig } from "@lib/views";

/** Entity fields needed to render the entity icon + name, keyed by entity uuid so schedule rows (which only carry `entityUuid`) can look them up. */
export interface ServiceTemplateScheduleEntityInfo {
    id: number;
    uuid: string;
    name: string;
    iconColor: string;
    logo: string | null;
    workspaceId: string;
}

export interface ServiceTemplateScheduleLeafRow extends ServiceTemplateSchedule {
    rowType: "schedule";
    children?: never;
}

/** Synthetic row wrapping every schedule sharing a non-null groupId. Carries the fields shared across the group (template/period) so the leaf-row cells render unchanged for it. */
export interface ServiceTemplateScheduleGroupRow extends Pick<
    ServiceTemplateSchedule,
    "templateId" | "period"
> {
    id: string;
    rowType: "group";
    groupId: string;
    children: ServiceTemplateScheduleLeafRow[];
}

export type ServiceTemplateScheduleRow =
    | ServiceTemplateScheduleLeafRow
    | ServiceTemplateScheduleGroupRow;

/**
 * Groups schedules sharing a non-null groupId into a single parent row (with
 * the member schedules nested as children), so the organization page can
 * display "applied to N entities" as one expandable row. Schedules with a
 * null groupId — and groupIds that happen to have only one member — render
 * as their own top-level row (nulls are never merged together, per PRO-10504).
 * Row order follows each group's first appearance in `schedules`.
 */
export function groupServiceTemplateSchedules(
    schedules: ServiceTemplateSchedule[],
): ServiceTemplateScheduleRow[] {
    const membersByGroupId = new Map<string, ServiceTemplateSchedule[]>();
    for (const schedule of schedules) {
        if (schedule.groupId === null) {
            continue;
        }
        const bucket = membersByGroupId.get(schedule.groupId);
        if (bucket) {
            bucket.push(schedule);
        } else {
            membersByGroupId.set(schedule.groupId, [schedule]);
        }
    }

    const toLeafRow = (
        schedule: ServiceTemplateSchedule,
    ): ServiceTemplateScheduleLeafRow => ({
        ...schedule,
        rowType: "schedule",
    });

    const emittedGroupIds = new Set<string>();
    const rows: ServiceTemplateScheduleRow[] = [];
    for (const schedule of schedules) {
        if (schedule.groupId === null) {
            rows.push(toLeafRow(schedule));
            continue;
        }
        if (emittedGroupIds.has(schedule.groupId)) {
            continue;
        }
        emittedGroupIds.add(schedule.groupId);
        // Every non-null groupId schedule was bucketed in the first loop, so
        // the key is always present here.
        const members = membersByGroupId.get(schedule.groupId)!;
        if (members.length === 1) {
            rows.push(toLeafRow(schedule));
            continue;
        }
        rows.push({
            id: `group:${schedule.groupId}`,
            rowType: "group",
            groupId: schedule.groupId,
            templateId: schedule.templateId,
            period: schedule.period,
            children: members.map(toLeafRow),
        });
    }
    return rows;
}

export const serviceTemplateSchedulesViewConfig: ViewConfig<
    ServiceTemplateScheduleRow,
    ServiceTemplateSchedulesViewColumn,
    ServiceTemplateSchedulesSortValue
> = {
    columns: {
        [ServiceTemplateSchedulesViewColumn.TEMPLATE_NAME]: {
            accessorKey: "templateId",
            name: "Service",
            header: "Service",
            sort: {
                asc: ServiceTemplateSchedulesSortValue.TEMPLATE_NAME_ASC,
                desc: ServiceTemplateSchedulesSortValue.TEMPLATE_NAME_DESC,
            },
            // Pinned left by default and can't be unpinned/hidden/reordered —
            // it's also the nesting expand column, so hiding or moving it
            // would break the group expand/collapse affordance. See PRO-10599.
            immutable: true,
            minSize: 200,
            maxSize: 400,
        },
        [ServiceTemplateSchedulesViewColumn.CLIENT]: {
            accessorKey: "workspaceId",
            name: "Client",
            header: "Client",
            sort: {
                asc: ServiceTemplateSchedulesSortValue.CLIENT_ASC,
                desc: ServiceTemplateSchedulesSortValue.CLIENT_DESC,
            },
            minSize: 180,
            maxSize: 320,
        },
        [ServiceTemplateSchedulesViewColumn.ENTITY]: {
            accessorKey: "entityUuid",
            name: "Entity",
            header: "Entity",
            sort: {
                asc: ServiceTemplateSchedulesSortValue.ENTITY_ASC,
                desc: ServiceTemplateSchedulesSortValue.ENTITY_DESC,
            },
            minSize: 180,
            maxSize: 320,
        },
        [ServiceTemplateSchedulesViewColumn.SCHEDULE]: {
            accessorKey: "interval",
            name: "Schedule",
            header: "Schedule",
            minSize: 160,
            maxSize: 320,
        },
        [ServiceTemplateSchedulesViewColumn.PERIOD]: {
            accessorKey: "period",
            name: "Period",
            header: "Period",
            sort: {
                asc: ServiceTemplateSchedulesSortValue.PERIOD_ASC,
                desc: ServiceTemplateSchedulesSortValue.PERIOD_DESC,
            },
            minSize: 120,
            maxSize: 180,
        },
        [ServiceTemplateSchedulesViewColumn.STATUS]: {
            accessorKey: "status",
            name: "Status",
            header: "Status",
            sort: {
                asc: ServiceTemplateSchedulesSortValue.STATUS_ASC,
                desc: ServiceTemplateSchedulesSortValue.STATUS_DESC,
            },
            minSize: 120,
            maxSize: 160,
        },
        // Merges the former separate "Next service start"/"Next service end"
        // columns into a single period column, displayed with
        // formatExactDateRangePeriod (via formatCloseTemplateDateRange) so an
        // exact month/quarter/year range collapses to e.g. "January 2026".
        // Sort still orders chronologically by the next occurrence's service
        // start date, reusing the existing START_DATE_ASC/DESC sort values.
        [ServiceTemplateSchedulesViewColumn.NEXT_SERVICE_PERIOD]: {
            accessorKey: "nextServiceStartDate",
            name: "Next service period",
            header: "Next service period",
            sort: {
                asc: ServiceTemplateSchedulesSortValue.START_DATE_ASC,
                desc: ServiceTemplateSchedulesSortValue.START_DATE_DESC,
            },
            minSize: 160,
            maxSize: 240,
        },
        [ServiceTemplateSchedulesViewColumn.NEXT_APPLICATION_DATE]: {
            accessorKey: "nextApplicationDate",
            name: "Next application",
            header: "Next application",
            sort: {
                asc: ServiceTemplateSchedulesSortValue.NEXT_APPLICATION_DATE_ASC,
                desc: ServiceTemplateSchedulesSortValue.NEXT_APPLICATION_DATE_DESC,
            },
            minSize: 140,
            maxSize: 200,
        },
        [ServiceTemplateSchedulesViewColumn.NEXT_WORK_START]: {
            accessorKey: "nextWorkStartDate",
            name: "Next work start",
            header: "Next work start",
            sort: {
                asc: ServiceTemplateSchedulesSortValue.NEXT_WORK_START_ASC,
                desc: ServiceTemplateSchedulesSortValue.NEXT_WORK_START_DESC,
            },
            minSize: 140,
            maxSize: 200,
        },
        [ServiceTemplateSchedulesViewColumn.NEXT_WORK_END]: {
            accessorKey: "nextWorkEndDate",
            name: "Next work end",
            header: "Next work end",
            sort: {
                asc: ServiceTemplateSchedulesSortValue.NEXT_WORK_END_ASC,
                desc: ServiceTemplateSchedulesSortValue.NEXT_WORK_END_DESC,
            },
            minSize: 140,
            maxSize: 200,
        },
        [ServiceTemplateSchedulesViewColumn.ENGAGEMENT_TEAM]: {
            // Synthetic key: the cell aggregates preparerId/reviewerId/managerId
            // from row.original, so no single field backs this column.
            accessorKey: "engagementTeam",
            name: "Engagement team",
            header: "Engagement team",
            minSize: 140,
            maxSize: 260,
        },
        [ServiceTemplateSchedulesViewColumn.ESTIMATED_TIME]: {
            // Derived from template tasks (never stored on the schedule) —
            // sorting is still backend-driven (the query sums
            // task_template.estimatedTime per schedule's template), so it
            // matches what this cell displays.
            accessorKey: "estimatedTime",
            name: "Estimated time",
            header: "Estimated time",
            sort: {
                asc: ServiceTemplateSchedulesSortValue.ESTIMATED_TIME_ASC,
                desc: ServiceTemplateSchedulesSortValue.ESTIMATED_TIME_DESC,
            },
            align: "right",
            minSize: 140,
            maxSize: 200,
        },
        [ServiceTemplateSchedulesViewColumn.CREATED_AT]: {
            accessorKey: "createdAt",
            name: "Created on",
            header: "Created on",
            sort: {
                asc: ServiceTemplateSchedulesSortValue.CREATED_AT_ASC,
                desc: ServiceTemplateSchedulesSortValue.CREATED_AT_DESC,
            },
            minSize: 140,
            maxSize: 200,
        },
        [ServiceTemplateSchedulesViewColumn.ACTIONS]: {
            accessorKey: "id",
            name: "Actions",
            header: "",
            minSize: 120,
            maxSize: 120,
            immutable: true,
            hiddenFromSettings: true,
        },
    },
};
